import { forwardRef } from "react";

/**
 * BookPageEn — English-mode 3D book page.
 *
 * Pair of `BookPage.jsx` (Hebrew, untouched). Same visual frame, same
 * forwardRef contract for react-pageflip — but renders the English
 * verse text (from Sefaria: Koren / Davidson / Mishnah-Yomit) instead
 * of Hebrew. RTL is dropped; the page reads left-to-right with classic
 * English serif typography.
 *
 * Pagination is done by the parent (BookReader3DEn) using English
 * character counts, which are noticeably longer than the equivalent
 * Hebrew, so fewer verses fit per page.
 *
 * Verses without an English translation are NOT hidden — we show the
 * original Hebrew with a respectful inline notice. This preserves the
 * sacred text rather than dropping it.
 */
const BookPageEn = forwardRef(function BookPageEn(
  {
    variant = "text",
    bookEn,
    chapter,
    pageIndex,
    side = "right",
    verses = [],
    startsChapter = false,
    activeVerseNum = null,
    onVerseClick,
    highlights = {},
    notesByVerse = {},
    showVerseNums = true,
    tocContent,
    chapterTitle,
    transitionDirection,
    onTransitionClick,
    transitionLabelEn,
    transitionTargetChapter,
    chapterLabelEn,
  },
  ref
) {
  return (
    <div ref={ref} className="book-page-paper" data-density="hard">
      <div
        className={`book-page-inner page-${side}`}
        style={{ position: "absolute", inset: 0 }}
      >
        {variant === "chapter-title" && (
          <div className="flex h-full flex-col items-center justify-center px-10 text-center">
            <p className="font-latin italic text-xs tracking-[0.35em] uppercase text-[hsl(36,75%,38%)] mb-4">
              {bookEn} · {chapterLabelEn || `Chapter ${chapter}`}
            </p>
            <h2
              className="font-heading text-5xl"
              style={{ color: "inherit", fontFamily: '"Cormorant Garamond", "Crimson Pro", serif' }}
            >
              {chapterTitle || bookEn}
            </h2>
            <div className="book-chapter-ornament mt-4 w-32">✦</div>
            <p className="font-heading text-2xl mt-3 text-[hsl(36,75%,38%)]">
              {chapterLabelEn || `Chapter ${chapter}`}
            </p>
          </div>
        )}

        {variant === "toc" && tocContent}

        {variant === "text" && (
          <>
            <div className="book-page-header" dir="ltr">
              <span className="book-header-title" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                {bookEn}
              </span>
              <span className="book-header-num">
                {chapterLabelEn || `Chapter ${chapter}`}
              </span>
            </div>

            {startsChapter && (
              <div className="book-chapter-title">
                <h3
                  className="he"
                  style={{ fontFamily: '"Cormorant Garamond", "Crimson Pro", serif', direction: "ltr" }}
                >
                  {chapterLabelEn || `Chapter ${chapter}`}
                </h3>
                <div className="book-chapter-ornament">✦</div>
              </div>
            )}

            <div
              className={`book-printed-verses book-printed-verses-en ${startsChapter ? "book-printed-dropcap-en" : ""}`}
              dir="ltr"
              lang="en"
              style={{
                fontFamily: '"Frank Ruhl Libre", "Crimson Pro", "Cormorant Garamond", "Times New Roman", serif',
                textAlign: "justify",
                lineHeight: 1.85,
              }}
            >
              {verses.map((v, idx) => {
                const hl = highlights[v.verse];
                const note = notesByVerse[v.verse];
                const active = activeVerseNum === v.verse;
                const hasEn = !!(v.en && v.en.trim());
                // Stop touch/pointer/mouse events from bubbling so a verse
                // tap never starts a page-flip swipe — same trick the
                // Hebrew `BookPage` uses.
                const stop = (e) => e.stopPropagation();
                return (
                  <span
                    key={v.verse}
                    data-testid={`book-verse-en-${v.verse}`}
                    onPointerDown={stop}
                    onMouseDown={stop}
                    onTouchStart={stop}
                    onClick={(e) => {
                      e.stopPropagation();
                      onVerseClick?.(v);
                    }}
                    className={`book-printed-verse ${active ? "is-active" : ""} ${hl ? `hl-${hl.color}` : ""}`}
                    data-ref={`${bookEn} ${chapter}:${v.verse}`}
                    style={{ fontSize: "0.92em" }}
                  >
                    {showVerseNums && (
                      <span
                        className="book-printed-verse-num"
                        style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "0.75em" }}
                      >
                        {v.verse}
                      </span>
                    )}
                    {hasEn ? (
                      <span>{v.en}</span>
                    ) : (
                      // No English available — show the original Hebrew
                      // (kept sacred + readable) followed by a respectful
                      // small italic note explaining why.
                      <>
                        <span dir="rtl" lang="he" style={{ fontFamily: '"Frank Ruhl Libre", "David Libre", serif' }}>
                          {v.he}
                        </span>
                        <em
                          className="text-[10px] italic opacity-70 ms-2"
                          style={{ fontSize: "0.65em", color: "hsl(36,40%,40%)" }}
                        >
                          {" — English translation not yet available; original Hebrew shown."}
                        </em>
                      </>
                    )}
                    {note && <span className="text-[hsl(36,75%,38%)]">◆</span>}
                    {idx < verses.length - 1 ? " " : ""}
                  </span>
                );
              })}
            </div>

            <div className="book-page-footer" dir="ltr">
              <span className="book-page-folio">{pageIndex + 1}</span>
            </div>
          </>
        )}

        {variant === "blank" && (
          <div className="flex h-full items-center justify-center">
            <div className="text-[hsl(36,60%,40%)] opacity-50 text-3xl">✦</div>
          </div>
        )}

        {variant === "transition" && (
          <div
            className="flex h-full flex-col items-center justify-center px-8 text-center gap-5"
            dir="ltr"
            data-testid={`book-transition-en-${transitionDirection}`}
          >
            <p className="font-latin italic text-[11px] tracking-[0.35em] uppercase text-[hsl(36,75%,38%)]">
              {transitionDirection === "next" ? "Continue reading" : "Previous chapter"}
            </p>
            <div className="book-chapter-ornament w-40">
              ✦ ❖ ✦
            </div>
            <h2
              className="font-heading text-3xl md:text-4xl leading-tight"
              style={{ color: "inherit", fontFamily: '"Cormorant Garamond", serif' }}
            >
              {transitionLabelEn || (transitionDirection === "next" ? "Next Chapter" : "Previous Chapter")}
            </h2>
            {transitionTargetChapter != null && (
              <p className="font-heading text-xl text-[hsl(36,75%,38%)]">
                {bookEn} · Chapter {transitionTargetChapter}
              </p>
            )}
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onTransitionClick?.();
              }}
              data-testid={`book-transition-en-btn-${transitionDirection}`}
              className="mt-3 inline-flex items-center gap-2 rounded-sm border border-[hsl(36,75%,38%)] bg-[hsl(36,75%,38%)]/10 px-5 py-2 text-sm font-heading text-[hsl(36,75%,38%)] hover:bg-[hsl(36,75%,38%)]/25 transition-colors"
            >
              {transitionDirection === "next" ? "Continue to next chapter" : "Back to previous chapter"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default BookPageEn;
